import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

// Константы
const CACHE_KEY = 'myAppCache';
const DEBUG = false;

// Добавляем декларацию типа для window.axios
declare global {
    interface Window {
        axios: typeof axios;
    }
}

/**
 * Структура хранимого кеша
 */
interface CacheData {
    [url: string]: {
        data: unknown;
        etag: string;
    };
}

/**
 * Менеджер кеша
 */
const cacheManager = {
    // Загрузка кеша из localStorage
    loadCache(): CacheData {
        try {
            const cache = localStorage.getItem(CACHE_KEY);
            if (DEBUG) console.log('[ETagCache] Загружаем кеш из localStorage', cache ? 'найден' : 'не найден');

            // Попытаемся преобразовать кеш и добавим больше логов
            const parsedCache = cache ? JSON.parse(cache) : {};
            if (DEBUG && Object.keys(parsedCache).length > 0) {
                console.log('[ETagCache] Содержимое кеша:', JSON.stringify(parsedCache, null, 2));
            }
            return parsedCache;
        } catch (e) {
            console.error('[ETagCache] Ошибка при загрузке кеша:', e);
            return {};
        }
    },

    // Сохранение кеша в localStorage
    saveCache(cache: CacheData): void {
        try {
            // Очистим данные от timestamp перед сохранением
            const cleanCache: CacheData = {};
            Object.keys(cache).forEach((url) => {
                cleanCache[url] = {
                    data: cache[url].data,
                    etag: cache[url].etag
                };
            });

            const cacheString = JSON.stringify(cleanCache);
            localStorage.setItem(CACHE_KEY, cacheString);
            if (DEBUG) console.log('[ETagCache] Кеш сохранен в localStorage:', cacheString);
        } catch (e) {
            console.error('[ETagCache] Ошибка при сохранении кеша:', e);
        }
    },

    // Получение ETag для URL
    getETag(url: string): string | null {
        const cache = this.loadCache();
        const entry = cache[url];

        // Более подробный лог
        if (DEBUG) {
            if (entry?.etag) {
                console.log(`[ETagCache] Найден ETag для ${url}: "${entry.etag}"`);
            } else {
                console.log(`[ETagCache] ETag для ${url} не найден`);
            }
        }

        return entry?.etag || null;
    },

    // Сохранение данных в кеш
    saveData(url: string, data: unknown, etag: string): void {
        const cache = this.loadCache();
        if (DEBUG) console.log(`[ETagCache] Сохраняем данные для ${url} с ETag "${etag}"`);

        cache[url] = {
            data,
            etag
        };

        this.saveCache(cache);
    },

    // Получение данных из кеша
    getData(url: string): unknown {
        const cache = this.loadCache();
        if (DEBUG) console.log(`[ETagCache] Получаем данные для ${url}:`, cache[url]?.data ? 'найдены' : 'не найдены');
        return cache[url]?.data || null;
    },

    // Очистка кеша
    clearCache(): void {
        if (DEBUG) console.log('[ETagCache] Очистка кеша');
        localStorage.removeItem(CACHE_KEY);
    }
};

// Создаем общий экземпляр axios для кеширования
const setupCachedAxios = () => {
    // Интерцептор запросов для добавления If-None-Match
    axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        if (DEBUG) console.log('[ETagCache] Обработка запроса:', config.url);
        if (config.method?.toLowerCase() === 'get' && config.url) {
            const etag = cacheManager.getETag(config.url);
            if (etag) {
                if (DEBUG) console.log(`[ETagCache] Добавляем If-None-Match: "${etag}" для ${config.url}`);
                config.headers = config.headers || {};
                // Важно: передаем ETag в том же виде, в котором получили
                config.headers['If-None-Match'] = etag;
            }
        }
        return config;
    });

    // Интерцептор ответов для обработки кеширования
    axios.interceptors.response.use(
        (response: AxiosResponse) => {
            if (response.config.method?.toLowerCase() === 'get' && response.config.url) {
                const url = response.config.url;
                let etag = null;

                // Логируем полный URL, чтобы убедиться, что кешируем правильно
                if (DEBUG) console.log(`[ETagCache] Получен ответ для URL: "${url}"`);
                if (DEBUG) console.log('[ETagCache] Заголовки ответа:', response.headers);

                try {
                    // Проверяем, есть ли etag в заголовках
                    if (response.headers['etag']) {
                        etag = response.headers['etag'];
                    } else if (typeof response.headers.get === 'function') {
                        etag = response.headers.get('etag');
                    } else {
                        // Проверяем все ключи заголовков
                        const keys = Object.keys(response.headers);
                        if (DEBUG) console.log('[ETagCache] Ключи заголовков:', keys);

                        // Ищем etag в заголовках (с разным регистром)
                        const etagKey = keys.find((k) => k.toLowerCase() === 'etag');
                        if (etagKey) {
                            etag = response.headers[etagKey];
                        }
                    }
                } catch (e) {
                    console.error('[ETagCache] Ошибка при получении ETag:', e);
                }

                if (DEBUG) console.log(`[ETagCache] Извлечённый ETag: "${etag}"`);

                if (etag && typeof etag === 'string') {
                    // Проверяем, отличается ли новый ETag от предыдущего
                    const oldEtag = cacheManager.getETag(url);
                    if (oldEtag && oldEtag !== etag && DEBUG) {
                        console.log(`[ETagCache] Внимание! ETag изменился для ${url}:`, {
                            старый: oldEtag,
                            новый: etag
                        });
                    }

                    cacheManager.saveData(url, response.data, etag);

                    // Проверяем, что сохранение прошло успешно
                    const savedEtag = cacheManager.getETag(url);
                    if (DEBUG) console.log(`[ETagCache] Проверка сохраненного ETag: "${savedEtag}"`);

                    if (savedEtag !== etag) {
                        console.error(`[ETagCache] Ошибка! Сохраненный ETag не совпадает с полученным:`, {
                            полученный: etag,
                            сохраненный: savedEtag
                        });
                    }
                } else {
                    if (DEBUG) console.log(`[ETagCache] ETag не найден для ${url}`);
                }
            }
            return response;
        },
        (error: AxiosError) => {
            if (error.response?.status === 304 && error.config?.url) {
                const url = error.config.url;
                const cachedData = cacheManager.getData(url);

                if (cachedData) {
                    if (DEBUG) console.log(`[ETagCache] Используем кешированные данные для ${url} (304 Not Modified)`);
                    return Promise.resolve({
                        ...error.response,
                        status: 200,
                        data: cachedData,
                        fromCache: true
                    });
                }
            }
            return Promise.reject(error);
        }
    );

    return axios;
};

// Настраиваем кеширование
setupCachedAxios();

/**
 * Хук для использования кешированного API с ETag
 */
export function useETagCachedAPI<T, P = unknown>(fetchFn: (params?: P) => Promise<T>) {
    return {
        async fetch(params?: P): Promise<T> {
            // Просто вызываем функцию запроса - глобальный axios уже настроен с кешированием
            return await fetchFn(params);
        }
    };
}

/**
 * Экспортируем функции для работы с кешем
 */
export const etagCache = {
    clear: cacheManager.clearCache.bind(cacheManager),
    getEntries: () => cacheManager.loadCache(),
    getData: cacheManager.getData.bind(cacheManager)
};
